import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Button, Space, Tag, message, Tabs, Image, Upload, Card, Select, Input,
  Empty, Divider, Tooltip,
} from 'antd'
import {
  UploadOutlined, SaveOutlined, ReloadOutlined, PlusOutlined,
  EyeOutlined, DeleteOutlined, UpOutlined, DownOutlined,
  LinkOutlined, PictureOutlined, MenuOutlined, FundViewOutlined,
} from '@ant-design/icons';
import { useApiSelector } from 'src/hooks'
import { useRequest, dispatchAsync } from 'friday-async'
import jsCookie from 'js-cookie'
import config from 'src/config'
import './index.less'

// sessionStorage 缓存 key
const DRAFT_CACHE_KEY = 'homepage_draft_v1'

// 首页各位置推荐图片尺寸（宽 × 高）
const IMAGE_SIZES = {
  banner: { width: 750, height: 499, label: '轮播图 750×499' },
  adSlot: { width: 750, height: 167, label: '广告图片 750×167' },
  activity: { width: 680, height: 207, label: '活动展示图 680×207' },
} as const

// 校验图片尺寸，返回 null 表示通过，返回 string 表示错误信息
const validateImageSize = (file: File, expected: { width: number; height: number; label: string }): Promise<boolean> => {
  return new Promise((resolve) => {
    const img = new window.Image()
    const url = URL.createObjectURL(file)
    img.onload = () => {
      URL.revokeObjectURL(url)
      if (img.width !== expected.width || img.height !== expected.height) {
        message.error(
          `图片尺寸不符：当前 ${img.width}×${img.height}，要求 ${expected.label}（${expected.width}×${expected.height}）`,
          5
        )
        resolve(false)
      } else {
        resolve(true)
      }
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      message.error('图片读取失败')
      resolve(false)
    }
    img.src = url
  })
}

// 从 sessionStorage 读取草稿（如果存在）
const loadDraftFromCache = (): any | null => {
  try {
    const raw = sessionStorage.getItem(DRAFT_CACHE_KEY)
    if (!raw) return null
    return JSON.parse(raw)
  } catch { return null }
}

const Homepage = () => {
  const apis = useApiSelector();
  // 加载首页配置
  const { data: configData, isValidating: configLoading, revalidate } = useRequest(apis.homepage.getConfig(), { paginated: false })
  // 加载所有轮播图
  const { dataArray: banners, revalidate: revalidateBanners } = useRequest(apis.banner.list(), { paginated: false })
  // 加载所有活动
  const { dataArray: activities } = useRequest(apis.activity.list(), { paginated: false })
  // 加载所有资讯
  const { dataArray: newsList, revalidate: revalidateNews } = useRequest(apis.news.list(), { paginated: false })
  // 加载所有底部菜单
  const { dataArray: menus, revalidate: revalidateMenus } = useRequest(apis.bottomMenu.list(), { paginated: false })

  // 读取一次 sessionStorage 草稿，作为初始值
  const initialDraftRef = useRef(loadDraftFromCache())

  // 各 Tab 的本地状态（保存的最终值）
  const [bannerIds, setBannerIds] = useState<string[]>(initialDraftRef.current?.bannerIds || [])
  const [hotActivityCodes, setHotActivityCodes] = useState<string[]>(initialDraftRef.current?.hotActivityCodes || [])
  const [hotNewsIds, setHotNewsIds] = useState<string[]>(initialDraftRef.current?.hotNewsIds || [])
  const [bottomMenuIds, setBottomMenuIds] = useState<string[]>(initialDraftRef.current?.bottomMenuIds || [])
  const [adSlot, setAdSlot] = useState<{ imageUrl: string; linkUrl: string }>(
    initialDraftRef.current?.adSlot || { imageUrl: '', linkUrl: '' }
  )

  // 临时草稿（用于实时预览，不入库）
  const [draft, setDraft] = useState({
    bannerIds: initialDraftRef.current?.bannerIds || ([] as string[]),
    hotActivityCodes: initialDraftRef.current?.hotActivityCodes || ([] as string[]),
    hotNewsIds: initialDraftRef.current?.hotNewsIds || ([] as string[]),
    bottomMenuIds: initialDraftRef.current?.bottomMenuIds || ([] as string[]),
    adSlot: (initialDraftRef.current?.adSlot || { imageUrl: '', linkUrl: '' }) as { imageUrl: string; linkUrl: string },
  })

  const [uploading, setUploading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [activeTab, setActiveTab] = useState(() => initialDraftRef.current?.activeTab || 'banner')
  const [initialized, setInitialized] = useState(false)

  // 草稿变更时持久化到 sessionStorage（即使切换菜单也不丢）
  useEffect(() => {
    const cached = {
      bannerIds, hotActivityCodes, hotNewsIds, bottomMenuIds, adSlot,
      activeTab,
    }
    try {
      sessionStorage.setItem(DRAFT_CACHE_KEY, JSON.stringify(cached))
    } catch { /* 忽略 */ }
  }, [bannerIds, hotActivityCodes, hotNewsIds, bottomMenuIds, adSlot, activeTab])

  // 首次加载时同步一次配置（如果没有 sessionStorage 草稿）
  useEffect(() => {
    console.log('configData', configData)
    if (configData && !initialized) {
      // configData 是 axios response.data，即 { success: true, data: {...} }
      // 需要取 data 字段
      const raw = configData as any
      const cfg = raw.data || raw
      const loaded = {
        bannerIds: cfg.bannerIds || [],
        hotActivityCodes: cfg.hotActivityCodes || [],
        hotNewsIds: cfg.hotNewsIds || [],
        bottomMenuIds: cfg.bottomMenuIds || [],
        adSlot: cfg.adSlot || { imageUrl: '', linkUrl: '' },
      }
      setBannerIds(loaded.bannerIds)
      setHotActivityCodes(loaded.hotActivityCodes)
      setHotNewsIds(loaded.hotNewsIds)
      setBottomMenuIds(loaded.bottomMenuIds)
      setAdSlot(loaded.adSlot)
      setDraft(loaded)
      setInitialized(true)
    }
  }, [configData, initialized])

  // 草稿变更同步（实时预览）
  const updateDraft = (patch: Partial<typeof draft>) => {
    setDraft(prev => ({ ...prev, ...patch }))
  }

  // 移动草稿项顺序
  const moveDraftItem = (key: keyof typeof draft, index: number, direction: 'up' | 'down') => {
    setDraft(prev => {
      const arr = [...(prev[key] as string[])]
      const to = direction === 'up' ? index - 1 : index + 1
      if (to < 0 || to >= arr.length) return prev
      ;[arr[index], arr[to]] = [arr[to], arr[index]]
      return { ...prev, [key]: arr }
    })
  }

  // 从草稿删除项
  const removeDraftItem = (key: keyof typeof draft, index: number) => {
    setDraft(prev => {
      const arr = [...(prev[key] as string[])]
      arr.splice(index, 1)
      return { ...prev, [key]: arr }
    })
  }

  // 解析图片 URL
  const resolveImage = (url?: string) => {
    if (!url) return ''
    if (url.startsWith('http')) return url
    return `${config.imgHost}${url.startsWith('/') ? url.slice(1) : url}`
  }

  // ====== 图片上传 ======
  const handleUpload = (info: any) => {
    if (info.file.status === 'uploading') {
      setUploading(true); return
    }
    if (info.file.status === 'done') {
      setUploading(false)
      const res = info.file.response
      if (res?.data?.url) {
        updateDraft({ adSlot: { imageUrl: res.data.url, linkUrl: draft.adSlot.linkUrl } })
        message.success('图片上传成功')
      }
    } else if (info.file.status === 'error') {
      setUploading(false)
      message.error('图片上传失败')
    }
  }

  // ====== 保存 ======
  const handleSave = async () => {
    setSaving(true)
    try {
      const { error } = await dispatchAsync(apis.homepage.save({
        bannerIds: draft.bannerIds,
        hotActivityCodes: draft.hotActivityCodes,
        hotNewsIds: draft.hotNewsIds,
        bottomMenuIds: draft.bottomMenuIds,
        adSlot: draft.adSlot,
      }))
      if (error) {
        message.error('保存失败')
      } else {
        // 先同步已保存值，让 isDirty 立即变为 false（按钮变 "已保存"）
        setBannerIds(draft.bannerIds)
        setHotActivityCodes(draft.hotActivityCodes)
        setHotNewsIds(draft.hotNewsIds)
        setBottomMenuIds(draft.bottomMenuIds)
        setAdSlot(draft.adSlot)
        message.success('首页配置已保存并生效')
        // 后台静默刷新最新配置
        revalidate()
      }
    } catch { message.error('保存失败') }
    setSaving(false)
  }

  // 重置草稿到已保存状态
  const handleReset = () => {
    setDraft({
      bannerIds,
      hotActivityCodes,
      hotNewsIds,
      bottomMenuIds,
      adSlot,
    })
  }

  // ====== 预览数据（基于草稿）======
  const previewBanners = useMemo(() => {
    return draft.bannerIds
      .map(id => (banners || []).find((b: any) => b._id === id))
      .filter(Boolean)
  }, [draft.bannerIds, banners])

  const previewActivities = useMemo(() => {
    return draft.hotActivityCodes
      .map(code => (activities || []).find((a: any) => a.code === code))
      .filter(Boolean)
  }, [draft.hotActivityCodes, activities])

  const previewNews = useMemo(() => {
    return draft.hotNewsIds
      .map(id => (newsList || []).find((n: any) => n._id === id))
      .filter(Boolean)
  }, [draft.hotNewsIds, newsList])

  const previewMenus = useMemo(() => {
    return draft.bottomMenuIds
      .map(id => (menus || []).find((m: any) => m._id === id))
      .filter(Boolean)
  }, [draft.bottomMenuIds, menus])

  const isDirty = useMemo(() => {
    return JSON.stringify({
      bannerIds, hotActivityCodes, hotNewsIds, bottomMenuIds, adSlot
    }) !== JSON.stringify(draft)
  }, [bannerIds, hotActivityCodes, hotNewsIds, bottomMenuIds, adSlot, draft])

  // ====== 轮播图配置 ======
  const renderBannerConfig = () => {
    const enabledBanners = (banners || []).filter((b: any) => b.status === 'enabled')
    const unselected = enabledBanners.filter((b: any) => !draft.bannerIds.includes(b._id))
    return (
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <div className="size-tip">
          📐 轮播图推荐尺寸：<b>{IMAGE_SIZES.banner.label}</b>（宽 × 高）。请在「轮播图管理」中按此尺寸上传
        </div>
        <div>
          <div className="config-label">添加轮播图（点击添加）</div>
          <div className="banner-picker">
            {unselected.length === 0 && <Empty description="暂无可添加的轮播图，请先在「轮播图管理」创建" image={Empty.PRESENTED_IMAGE_SIMPLE} />}
            {unselected.map((b: any) => (
              <div key={b._id} className="banner-pick-item" onClick={() => updateDraft({ bannerIds: [...draft.bannerIds, b._id] })}>
                <Image src={resolveImage(b.imageUrl)} width={60} height={36} style={{ objectFit: 'cover', borderRadius: 4 }} preview={false} />
                <div className="banner-pick-title">{b.title}</div>
                <PlusOutlined style={{ color: '#1677FF' }} />
              </div>
            ))}
          </div>
        </div>

        <Divider style={{ margin: '8px 0' }} />

        <div>
          <div className="config-label">已选轮播图（拖动/上下移动调整顺序）</div>
          {previewBanners.length === 0 && <Empty description="未选择任何轮播图" image={Empty.PRESENTED_IMAGE_SIMPLE} />}
          {previewBanners.map((b: any, idx: number) => (
            <div key={b._id} className="banner-list-item">
              <Tag color="blue">{idx + 1}</Tag>
              <Image src={resolveImage(b.imageUrl)} width={80} height={48} style={{ objectFit: 'cover', borderRadius: 4 }} preview={false} />
              <div className="banner-list-info">
                <div className="banner-list-title">{b.title}</div>
                {b.linkUrl && <div className="banner-list-link"><LinkOutlined /> {b.linkUrl}</div>}
              </div>
              <Space>
                <Tooltip title="上移">
                  <Button size="small" icon={<UpOutlined />} disabled={idx === 0} onClick={() => moveDraftItem('bannerIds', idx, 'up')} />
                </Tooltip>
                <Tooltip title="下移">
                  <Button size="small" icon={<DownOutlined />} disabled={idx === previewBanners.length - 1} onClick={() => moveDraftItem('bannerIds', idx, 'down')} />
                </Tooltip>
                <Tooltip title="移除">
                  <Button size="small" danger icon={<DeleteOutlined />} onClick={() => removeDraftItem('bannerIds', idx)} />
                </Tooltip>
              </Space>
            </div>
          ))}
        </div>
      </Space>
    )
  }

  // ====== 热门活动配置 ======
  const renderActivityConfig = () => {
    return (
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <div className="size-tip">
          📐 首页活动展示图推荐尺寸：<b>{IMAGE_SIZES.activity.label}</b>（宽 × 高）。请在「活动管理」中按此尺寸上传
        </div>
        <div>
          <div className="config-label">选择热门活动</div>
          <Select
            mode="multiple"
            style={{ width: '100%' }}
            placeholder="输入活动名称搜索"
            value={draft.hotActivityCodes}
            onChange={(vals) => updateDraft({ hotActivityCodes: vals })}
            optionFilterProp="label"
            showSearch
            options={(activities || []).filter((a: any) => a.status === 'active').map((a: any) => ({
              label: a.name || a.title,
              value: a.code,
            }))}
          />
        </div>
        <div>
          <div className="config-label">已选活动（可调整顺序）</div>
          {previewActivities.length === 0 && <Empty description="未选择任何活动" image={Empty.PRESENTED_IMAGE_SIMPLE} />}
          {previewActivities.map((a: any, idx: number) => (
            <div key={a.code} className="banner-list-item">
              <Tag color="blue">{idx + 1}</Tag>
              <Image src={resolveImage(a.displayImage)} width={48} height={48} style={{ objectFit: 'cover', borderRadius: 4 }} preview={false} />
              <div className="banner-list-info">
                <div className="banner-list-title">{a.name || a.title}</div>
                <div className="banner-list-link">标识：{a.code}</div>
              </div>
              <Space>
                <Button size="small" icon={<UpOutlined />} disabled={idx === 0} onClick={() => moveDraftItem('hotActivityCodes', idx, 'up')} />
                <Button size="small" icon={<DownOutlined />} disabled={idx === previewActivities.length - 1} onClick={() => moveDraftItem('hotActivityCodes', idx, 'down')} />
                <Button size="small" danger icon={<DeleteOutlined />} onClick={() => removeDraftItem('hotActivityCodes', idx)} />
              </Space>
            </div>
          ))}
        </div>
      </Space>
    )
  }

  // ====== 热门资讯配置 ======
  const renderNewsConfig = () => {
    return (
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <div>
          <div className="config-label">选择热门资讯（最多 4 个，按四宫格展示）</div>
          <Select
            mode="multiple"
            style={{ width: '100%' }}
            placeholder="输入资讯标题搜索"
            value={draft.hotNewsIds}
            onChange={(vals) => updateDraft({ hotNewsIds: vals.slice(0, 4) })}
            maxCount={4}
            optionFilterProp="label"
            showSearch
            options={(newsList || []).map((n: any) => ({
              label: n.title,
              value: n._id,
            }))}
          />
        </div>
        <div>
          <div className="config-label">已选资讯（最多 4 个）</div>
          {previewNews.length === 0 && <Empty description="未选择任何资讯" image={Empty.PRESENTED_IMAGE_SIMPLE} />}
          <div className="news-preview-grid">
            {previewNews.map((n: any, idx: number) => (
              <div key={n._id} className="news-preview-item">
                <Image src={resolveImage(n.displayImage)} width="100%" height={80} style={{ objectFit: 'cover' }} preview={false} />
                <div className="news-preview-title">{n.title}</div>
                <Button size="small" danger icon={<DeleteOutlined />} block onClick={() => removeDraftItem('hotNewsIds', idx)}>移除</Button>
              </div>
            ))}
            {Array.from({ length: Math.max(0, 4 - previewNews.length) }).map((_, idx) => (
              <div key={`empty-${idx}`} className="news-preview-item news-preview-empty">
                <span>空位 {previewNews.length + idx + 1}</span>
              </div>
            ))}
          </div>
        </div>
      </Space>
    )
  }

  // ====== 底部菜单配置 ======
  const renderMenuConfig = () => {
    const enabledMenus = (menus || []).filter((m: any) => m.status === 'enabled')
    const unselected = enabledMenus.filter((m: any) => !draft.bottomMenuIds.includes(m._id))
    return (
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <div>
          <div className="config-label">添加底部菜单</div>
          <div className="banner-picker">
            {unselected.length === 0 && <Empty description="暂无可添加的菜单" image={Empty.PRESENTED_IMAGE_SIMPLE} />}
            {unselected.map((m: any) => (
              <div key={m._id} className="banner-pick-item" onClick={() => updateDraft({ bottomMenuIds: [...draft.bottomMenuIds, m._id] })}>
                <Image src={resolveImage(m.icon)} width={32} height={32} style={{ objectFit: 'contain' }} preview={false} />
                <div className="banner-pick-title">{m.title}</div>
                <PlusOutlined style={{ color: '#1677FF' }} />
              </div>
            ))}
          </div>
        </div>

        <Divider style={{ margin: '8px 0' }} />

        <div>
          <div className="config-label">已选菜单（建议 3-5 个）</div>
          {previewMenus.length === 0 && <Empty description="未选择任何菜单" image={Empty.PRESENTED_IMAGE_SIMPLE} />}
          {previewMenus.map((m: any, idx: number) => (
            <div key={m._id} className="banner-list-item">
              <Tag color="blue">{idx + 1}</Tag>
              <Image src={resolveImage(m.icon)} width={36} height={36} style={{ objectFit: 'contain' }} preview={false} />
              <div className="banner-list-info">
                <div className="banner-list-title">{m.title}</div>
                <div className="banner-list-link"><LinkOutlined /> {m.linkUrl}</div>
              </div>
              <Space>
                <Button size="small" icon={<UpOutlined />} disabled={idx === 0} onClick={() => moveDraftItem('bottomMenuIds', idx, 'up')} />
                <Button size="small" icon={<DownOutlined />} disabled={idx === previewMenus.length - 1} onClick={() => moveDraftItem('bottomMenuIds', idx, 'down')} />
                <Button size="small" danger icon={<DeleteOutlined />} onClick={() => removeDraftItem('bottomMenuIds', idx)} />
              </Space>
            </div>
          ))}
        </div>
      </Space>
    )
  }

  // ====== 广告图片配置 ======
  const renderAdSlotConfig = () => (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <Card title="广告图片（轮播图下方展示）" size="small">
        <Space direction="vertical" style={{ width: '100%' }}>
          <div className="size-tip">
            📐 推荐尺寸：<b>{IMAGE_SIZES.adSlot.label}</b>（宽 × 高），其他尺寸将被拒绝上传
          </div>
          <Upload
            name="image"
            showUploadList={false}
            headers={{ Authorization: `Bearer ${jsCookie.get('token_jwt') || ''}` }}
            action={`${config.host}/api/platform/activity/uploadImage`}
            beforeUpload={(file) => validateImageSize(file, IMAGE_SIZES.adSlot)}
            onChange={handleUpload}
          >
            <Button icon={<UploadOutlined />} loading={uploading}>上传图片</Button>
          </Upload>
          {draft.adSlot.imageUrl && (
            <div>
              <Image
                src={resolveImage(draft.adSlot.imageUrl)}
                style={{ maxWidth: 300, borderRadius: 4 }}
              />
              <Button danger size="small" icon={<DeleteOutlined />} style={{ marginTop: 8 }} onClick={() => updateDraft({ adSlot: { imageUrl: '', linkUrl: draft.adSlot.linkUrl } })}>清除图片</Button>
            </div>
          )}
        </Space>
      </Card>
      <Card title="跳转链接" size="small">
        <Select
          style={{ width: '100%' }}
          placeholder="选择资讯详情页"
          allowClear
          value={draft.adSlot.linkUrl || undefined}
          onChange={(val) => updateDraft({ adSlot: { imageUrl: draft.adSlot.imageUrl, linkUrl: val || '' } })}
          options={(newsList || []).map((n: any) => ({
            label: n.title || '无标题',
            value: `/pages/news/detail/detail?id=${n._id}`,
          }))}
        />
        <div style={{ color: '#999', fontSize: 12, marginTop: 8 }}>
          选择广告图片跳转的资讯详情页
        </div>
      </Card>
    </Space>
  )

  // ====== 移动端预览组件 ======
  const renderPhonePreview = () => {
    const currentBannerIdx = 0
    const currentBanner = previewBanners[currentBannerIdx]
    return (
      <div className="phone-frame">
        <div className="phone-notch" />
        <div className="phone-screen">
          {/* 状态栏 */}
          <div className="phone-statusbar">
            <span>9:41</span>
            <span>●●● 5G 100%</span>
          </div>

          {/* 顶部导航 */}
          <div className="phone-topbar">
            <span style={{ fontSize: 12, color: '#666' }}>›</span>
            <span style={{ flex: 1, textAlign: 'center', fontSize: 14, fontWeight: 500 }}>首页</span>
            <span style={{ fontSize: 12, color: '#666' }}>···</span>
          </div>

          {/* 轮播图 */}
          <div className="phone-banner">
            {currentBanner ? (
              <img src={resolveImage((currentBanner as any).imageUrl)} alt="banner" />
            ) : (
              <div className="phone-empty">未配置轮播图</div>
            )}
            {previewBanners.length > 1 && (
              <div className="phone-banner-dots">
                {previewBanners.map((_: any, i: number) => (
                  <span key={i} className={`phone-dot ${i === currentBannerIdx ? 'active' : ''}`} />
                ))}
              </div>
            )}
          </div>

          {/* 广告图片 */}
          <div className="phone-adslot">
            {draft.adSlot.imageUrl ? (
              <img src={resolveImage(draft.adSlot.imageUrl)} alt="ad" />
            ) : (
              <div className="phone-empty">未配置广告图片</div>
            )}
          </div>

          {/* 热门活动 */}
          <div className="phone-section">
            <div className="phone-section-title">热门活动</div>
            {previewActivities.length === 0 ? (
              <div className="phone-empty" style={{ padding: '20px 12px' }}>暂无活动</div>
            ) : (
              previewActivities.slice(0, 3).map((a: any) => (
                <div key={a.code} className="phone-activity-card">
                  <img src={resolveImage(a.displayImage)} alt={a.name} />
                </div>
              ))
            )}
          </div>

          {/* 最新资讯 */}
          <div className="phone-section">
            <div className="phone-section-title">
              <span>最新资讯</span>
              <span className="phone-section-more">更多 ›</span>
            </div>
            {previewNews.length === 0 ? (
              <div className="phone-empty" style={{ padding: '20px 12px' }}>暂无资讯</div>
            ) : (
              <div className="phone-news-grid">
                {previewNews.map((n: any) => (
                  <div key={n._id} className="phone-news-item">
                    <img src={resolveImage(n.displayImage)} alt={n.title} />
                      <div className="phone-news-title">{n.title}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{ height: 50 }} />
        </div>

        {/* 底部 TabBar */}
        <div className="phone-tabbar">
          {previewMenus.length > 0 ? (
            previewMenus.map((m: any) => (
              <div key={m._id} className="phone-tab-item">
                <img src={resolveImage(m.icon)} alt={m.title} />
                <span>{m.title}</span>
              </div>
            ))
          ) : (
            <>
              <div className="phone-tab-item"><div className="phone-tab-icon" /><span>首页</span></div>
              <div className="phone-tab-item"><div className="phone-tab-icon" /><span>活动</span></div>
              <div className="phone-tab-item"><div className="phone-tab-icon" /><span>我的</span></div>
            </>
          )}
        </div>
      </div>
    )
  }

  const configPanels: Record<string, React.ReactNode> = {
    banner: renderBannerConfig(),
    hotActivity: renderActivityConfig(),
    hotNews: renderNewsConfig(),
    bottomMenu: renderMenuConfig(),
    adSlot: renderAdSlotConfig(),
  }

  return (
    <div className="homepage-page">
      {/* 顶部操作栏 */}
      <div className="homepage-toolbar">
        <Space>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={saving}
            disabled={!isDirty}
            onClick={handleSave}
          >
            {isDirty ? '保存配置' : '已保存'}
          </Button>
          <Button icon={<ReloadOutlined />} onClick={handleReset} disabled={!isDirty}>
            撤销修改
          </Button>
        </Space>
        <Space>
          {isDirty && <Tag color="orange">有未保存的修改</Tag>}
        </Space>
      </div>

      {/* 主体：左侧配置 + 右侧预览 */}
      <div className="homepage-body">
        <div className="homepage-config" style={{ flex: '0 0 60%' }}>
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            type="card"
            items={[
              { key: 'banner', label: <span><PictureOutlined /> 轮播图</span> },
              { key: 'hotActivity', label: <span><LinkOutlined /> 热门活动</span> },
              { key: 'hotNews', label: <span><EyeOutlined /> 最新资讯</span> },
              { key: 'bottomMenu', label: <span><MenuOutlined /> 底部菜单</span> },
              { key: 'adSlot', label: <span><FundViewOutlined /> 广告图片</span> },
            ]}
          />
          <div className="config-content">
            {configPanels[activeTab]}
          </div>
        </div>

        <div className="homepage-preview">
          <div className="preview-title">
            <EyeOutlined /> 实时预览（小程序首页）
          </div>
          {renderPhonePreview()}
          <div className="preview-hint">
            预览基于草稿数据，<span style={{ color: '#1677FF' }}>点击保存后才会生效</span>到小程序
          </div>
        </div>
      </div>
    </div>
  )
}

export default Homepage
